import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { getApi, postApi } from '../../Services/apiService';
import './ScreeningFormModal.css';

const QUESTIONS_ENDPOINT = '/screening/get-screening-questions';
const SUBMIT_ENDPOINT = '/screening/submit-form';

// How long the confirmation stays up before redirecting to the property details page.
const REDIRECT_DELAY_MS = 5000;

const TYPE_TEXT = 1;
const TYPE_MCQ = 2;
const TYPE_YES_NO = 3;
const YES_NO_ANSWERS = ['Yes', 'No'];

function isTruthyFlag(value) {
  return value === '1' || value === 1 || value === true;
}

// Options may come back as plain strings or as objects carrying the option id the
// submit endpoint expects. `value` is what we track in state: the id when the API
// provides one, otherwise the label.
function normalizeOption(option) {
  const label =
    typeof option === 'string'
      ? option
      : option?.option_name || option?.name || option?.text || option?.label || '';
  const id = typeof option === 'string' ? null : option?.id ?? option?.option_id ?? null;

  return { id, label, value: id || label };
}

function normalizeQuestion(question, index) {
  const type = Number(question?.type) || TYPE_MCQ;
  const options = (question?.options || question?.answers || [])
    .map(normalizeOption)
    .filter((option) => option.label);

  return {
    id: question?.id ?? `q-${index}`,
    question: question?.question || '',
    type,
    required: isTruthyFlag(question?.is_required ?? question?.required),
    options:
      type === TYPE_YES_NO && options.length === 0
        ? YES_NO_ANSWERS.map(normalizeOption)
        : options,
  };
}

function extractQuestions(response) {
  const root = response?.data || response;
  const list = Array.isArray(root)
    ? root
    : root?.questions || root?.data?.questions || root?.data || [];
  return (Array.isArray(list) ? list : []).map(normalizeQuestion).filter((q) => q.question);
}

const ScreeningFormModal = ({ isOpen, onClose, propertyId, screeningId, onSubmitted }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const redirectTimerRef = useRef(null);

  const fetchQuestions = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = {};
      if (propertyId) params.property_id = propertyId;
      if (screeningId) params.screening_id = screeningId;

      const res = await getApi(QUESTIONS_ENDPOINT, { params });
      const list = extractQuestions(res);

      if (!list.length) {
        setQuestions([]);
        setError(res?.message || 'No screening questions are available right now.');
        return;
      }

      setQuestions(list);
      setAnswers(
        list.reduce((acc, q) => {
          acc[q.id] = '';
          return acc;
        }, {})
      );
    } catch (e) {
      const msg =
        e?.response?.data?.message || e?.message || 'Failed to load screening questions';
      setError(msg);
      setQuestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [propertyId, screeningId]);

  useEffect(() => {
    if (!isOpen) return;
    setAnswers({});
    setIsSubmitted(false);
    fetchQuestions();
  }, [isOpen, fetchQuestions]);

  useEffect(
    () => () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    },
    []
  );

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  // Choice questions submit the selected `option_id`; free-text questions submit `answer`.
  // Unanswered optional questions are left out of the payload entirely.
  const buildAnswersPayload = () =>
    questions.reduce((acc, question) => {
      const selected = String(answers[question.id] ?? '').trim();
      if (!selected) return acc;

      if (question.type === TYPE_TEXT) {
        acc.push({ question_id: question.id, answer: selected });
        return acc;
      }

      const option = question.options.find((o) => o.value === selected);
      if (!option) return acc;

      acc.push(
        option.id
          ? { question_id: question.id, option_id: option.id }
          : { question_id: question.id, answer: option.label }
      );
      return acc;
    }, []);

  const finishSubmission = (res) => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    onSubmitted?.(res);
    onClose?.();
  };

  // Closing after a successful submit still takes the tenant to the property details page.
  const handleRequestClose = () => {
    if (isSubmitting) return;
    if (isSubmitted) {
      finishSubmission();
      return;
    }
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const missing = questions.find(
      (q) => q.required && !String(answers[q.id] || '').trim()
    );
    if (missing) {
      toast.error('Please answer all required questions.');
      return;
    }

    const answersPayload = buildAnswersPayload();
    if (!answersPayload.length) {
      toast.error('Please answer at least one question before submitting.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await postApi(SUBMIT_ENDPOINT, {
        property_id: propertyId,
        answers: answersPayload,
      });
      setIsSubmitted(true);
      // Give the tenant a moment to read the confirmation before we redirect.
      redirectTimerRef.current = setTimeout(() => finishSubmission(res), REDIRECT_DELAY_MS);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to submit screening form'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const answeredCount = useMemo(
    () => questions.filter((q) => String(answers[q.id] || '').trim()).length,
    [questions, answers]
  );
  const progressPercent = questions.length
    ? Math.round((answeredCount / questions.length) * 100)
    : 0;

  const renderAnswerField = (question) => {
    if (question.type === TYPE_TEXT) {
      return (
        <textarea
          className="screening-form-modal-textarea"
          rows={3}
          value={answers[question.id] || ''}
          onChange={(ev) => handleAnswerChange(question.id, ev.target.value)}
          placeholder="Type your answer"
        />
      );
    }

    return (
      <div className="screening-form-modal-options">
        {question.options.map((option) => (
          <label
            className={`screening-form-modal-option${
              answers[question.id] === option.value ? ' is-selected' : ''
            }`}
            key={`${question.id}-${option.value}`}
          >
            <input
              type="radio"
              name={`question-${question.id}`}
              value={option.value}
              checked={answers[question.id] === option.value}
              onChange={() => handleAnswerChange(question.id, option.value)}
            />
            <span className="screening-form-modal-option-mark" aria-hidden="true" />
            <span className="screening-form-modal-option-label">{option.label}</span>
          </label>
        ))}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={isSubmitting ? undefined : handleRequestClose}
      contentLabel="Screening Form"
      shouldCloseOnOverlayClick={!isSubmitting}
      ariaHideApp={false}
      className="screening-form-modal"
      overlayClassName="screening-form-modal-overlay"
    >
      <div className="screening-form-modal-inner">
        {isSubmitted ? (
          <div className="screening-form-modal-success">
            <span className="screening-form-modal-success-icon" aria-hidden="true">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <h2 className="screening-form-modal-success-title">Answers submitted</h2>
            <p className="screening-form-modal-success-text">
              Your answers have been submitted and will be reviewed using AI. This may take some
              time — you will be notified once the review is complete.
            </p>
            <button
              type="button"
              className="screening-form-modal-submit"
              onClick={() => finishSubmission()}
            >
              Back to property details
            </button>
          </div>
        ) : (
          <>
            <div className="screening-form-modal-header">
              <div className="screening-form-modal-heading">
                <h2 className="screening-form-modal-title">Screening Form</h2>
                <p className="screening-form-modal-subtitle">
                  Please answer the questions below clearly and accurately.
                </p>
              </div>
              <button
                type="button"
                className="screening-form-modal-close"
                onClick={handleRequestClose}
                disabled={isSubmitting}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            {!isLoading && !error && questions.length > 0 && (
              <div className="screening-form-modal-progress">
                <div className="screening-form-modal-progress-track">
                  <div
                    className="screening-form-modal-progress-bar"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="screening-form-modal-progress-text">
                  {answeredCount} of {questions.length} answered
                </span>
              </div>
            )}

            {isLoading && (
              <div className="screening-form-modal-body is-centered">
                <div className="screening-form-modal-state">
                  <span className="screening-form-modal-spinner" aria-hidden="true" />
                  Loading questions...
                </div>
              </div>
            )}

            {!isLoading && error && (
              <div className="screening-form-modal-body is-centered">
                <div className="screening-form-modal-state screening-form-modal-error">
                  <span>{error}</span>
                  <button type="button" className="screening-form-modal-retry" onClick={fetchQuestions}>
                    Try again
                  </button>
                </div>
              </div>
            )}

            {!isLoading && !error && questions.length > 0 && (
              <form className="screening-form-modal-form" onSubmit={handleSubmit}>
                <div className="screening-form-modal-body">
                  <div className="screening-form-modal-questions">
                    {questions.map((question, index) => (
                      <div className="screening-form-modal-question" key={question.id}>
                        <div className="screening-form-modal-question-head">
                          <span className="screening-form-modal-question-index">{index + 1}</span>
                          <p className="screening-form-modal-question-text">
                            {question.question}
                            {question.required && (
                              <span className="screening-form-modal-required"> *</span>
                            )}
                          </p>
                        </div>
                        {renderAnswerField(question)}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="screening-form-modal-footer">
                  <button
                    type="button"
                    className="screening-form-modal-cancel"
                    onClick={handleRequestClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="screening-form-modal-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            )}

            {isSubmitting && (
              <div className="screening-form-modal-overlay-loader">
                <span className="screening-form-modal-spinner" aria-hidden="true" />
                <p className="screening-form-modal-overlay-text">Submitting your answers...</p>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ScreeningFormModal;
